import React from 'react';
import { AimOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import type { Order, Session } from 'state';

import { formatters } from 'services';
import {
  currentSimplifiedOptionOrders,
  isSimplifiedOptionSettlementFailed,
  simplifiedOptionCurrencySymbol,
  simplifiedOptionSnapshotActionLabel,
  simplifiedOptionSnapshotContracts,
} from '../../../state/app/simplified_options/presentation';

import { sessionBlocksTrading } from './model';
import type { MarketInstrument, SelectInstrument } from './types';

type Props = {
  rows: MarketInstrument[];
  session: Session;
  currencies: Record<number, { symbol: string }>;
  reportingCurrencySymbol: string;
  orders: Order[];
  selectedInstrumentId?: number;
  selectInstrument: SelectInstrument;
  showOrderModal: () => void;
};

const money = (value: number | string | null | undefined, currency: string) =>
  value == null ? '—' : `${formatters.commas2Digits(value)} ${currency}`;

const pnlClass = (value: number) =>
  value > 0 ? 'is-positive' : value < 0 ? 'is-negative' : '';

const OptionsMarket: React.FC<Props> = ({
  rows,
  session,
  currencies,
  reportingCurrencySymbol,
  orders,
  selectedInstrumentId,
  selectInstrument,
  showOrderModal,
}) => {
  const activeLegs = rows.flatMap(row => {
    const positionId = row.instrument.simplified_option?.active_position_id;
    return currentSimplifiedOptionOrders(orders, positionId ?? null).map(
      order => ({ row, order }),
    );
  });

  return (
    <>
      <div className="markets-v2-option-grid">
        {rows.map(row => {
          const state = row.instrument.simplified_option;
          if (!state) return null;

          const nativeCurrency = simplifiedOptionCurrencySymbol(
            state.native_currency || row.asset.variable_currency,
            currencies,
          );
          const available = state.availability?.available === true;
          const failed = isSimplifiedOptionSettlementFailed(state);
          const tradeDisabled =
            sessionBlocksTrading(session.status) || !available || failed;
          const selected = selectedInstrumentId === row.instrument.id;
          const focus = () => selectInstrument(row.instrument);
          const contracts = state.contracts || 0;
          const direction =
            contracts > 0 ? 'Long' : contracts < 0 ? 'Short' : 'Square';
          const positionColor =
            contracts > 0 ? 'green' : contracts < 0 ? 'red' : undefined;
          const hasReportingPremium = state.reporting_premium != null;
          const netPremiumCurrency = hasReportingPremium
            ? reportingCurrencySymbol
            : nativeCurrency;

          return (
            <article
              key={row.instrument.id}
              className={`markets-v2-option-card${
                selected ? ' is-selected' : ''
              }${available ? '' : ' is-unavailable'}`}
              onClick={focus}
            >
              <div className="markets-v2-option-card__head">
                <div className="markets-v2-instrument-id">
                  <span className="markets-v2-ticker">{row.asset.ticker}</span>
                  <h3>{row.asset.name || row.asset.ticker}</h3>
                  <span className="markets-v2-instrument-sub">
                    {row.asset.underlying_kind === 'BOND'
                      ? 'Rates option'
                      : 'Equity option'}{' '}
                    · {row.asset.option_type === 0 ? 'Call' : 'Put'}
                  </span>
                </div>
                <span
                  className={`markets-v2-quote-state ${
                    failed ? 'is-failed' : available ? 'is-live' : 'is-waiting'
                  }`}
                  title={state.availability?.message}
                >
                  <i />
                  {failed
                    ? 'Settlement blocked'
                    : available
                      ? 'Issued'
                      : 'Unavailable'}
                </span>
              </div>

              <div className="markets-v2-option-terms">
                <div>
                  <span>Strike</span>
                  <strong>
                    {state.current_strike == null
                      ? '—'
                      : formatters.commas2Digits(state.current_strike)}
                  </strong>
                </div>
                <div>
                  <span>Annualized IV</span>
                  <strong>
                    {state.current_annualized_implied_volatility == null
                      ? '—'
                      : formatters.percent2Digit(
                          state.current_annualized_implied_volatility,
                        )}
                  </strong>
                </div>
                <div>
                  <span>Expiry</span>
                  <strong>
                    {state.days_to_expiry == null
                      ? '—'
                      : `${state.days_to_expiry} days`}
                  </strong>
                </div>
                <div>
                  <span>Premium / unit</span>
                  <strong>
                    {money(state.premium_per_controlled_unit, nativeCurrency)}
                  </strong>
                </div>
              </div>

              <div className="markets-v2-option-position">
                <div className="markets-v2-option-position__main">
                  <span>Position</span>
                  <strong>
                    <Tag color={positionColor}>{direction}</Tag>
                    {formatters.commasNoDigits(Math.abs(contracts))} contracts
                  </strong>
                </div>
                <dl>
                  <div>
                    <dt>Exposure · {reportingCurrencySymbol}</dt>
                    <dd>
                      {formatters.commasNoDigits(
                        state.reporting_controlled_exposure ??
                          state.controlled_exposure,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Net premium</dt>
                    <dd>
                      {money(
                        hasReportingPremium
                          ? state.reporting_premium
                          : (state.native_premium ?? state.premium),
                        netPremiumCurrency,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Payoff · {reportingCurrencySymbol}</dt>
                    <dd
                      className={pnlClass(
                        row.instrument.reporting_unrealized_pnl,
                      )}
                    >
                      {formatters.commas2Digits(
                        row.instrument.reporting_unrealized_pnl,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Realized · {reportingCurrencySymbol}</dt>
                    <dd
                      className={pnlClass(
                        row.instrument.reporting_realized_pnl,
                      )}
                    >
                      {formatters.commas2Digits(
                        row.instrument.reporting_realized_pnl,
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="markets-v2-option-card__actions">
                <Tooltip
                  title={
                    sessionBlocksTrading(session.status)
                      ? 'Trading is unavailable while this session is paused or closed.'
                      : failed
                        ? 'This option is quarantined after a settlement failure.'
                        : !available
                          ? 'This option becomes tradable when its next quote is issued.'
                          : 'Trade this option'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      type="primary"
                      icon={<SwapOutlined />}
                      disabled={tradeDisabled}
                      onClick={event => {
                        event.stopPropagation();
                        focus();
                        showOrderModal();
                      }}
                    >
                      Trade option
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Focus the market charts on this instrument">
                  <Button
                    size="small"
                    type={selected ? 'default' : 'text'}
                    className={selected ? 'is-selected' : ''}
                    aria-label={`Focus ${row.asset.name || row.asset.ticker}`}
                    icon={<AimOutlined />}
                    onClick={event => {
                      event.stopPropagation();
                      focus();
                    }}
                  />
                </Tooltip>
              </div>
            </article>
          );
        })}
      </div>

      {activeLegs.length > 0 && (
        <div className="markets-v2-option-ledger">
          <div className="markets-v2-option-ledger__title">
            Active option trade audit
            <span>{activeLegs.length} legs · always visible</span>
          </div>
          <div className="markets-v2-option-ledger__scroll">
            <table>
              <thead>
                <tr>
                  <th>Instrument</th>
                  <th>Action</th>
                  <th className="is-number">Contracts</th>
                  <th className="is-number">Premium</th>
                  <th>Registered expiry</th>
                  <th className="is-number">Payoff</th>
                </tr>
              </thead>
              <tbody>
                {activeLegs.map(({ row, order }) => {
                  const snapshot = order.simplified_option_snapshot;
                  const symbol = simplifiedOptionCurrencySymbol(
                    snapshot?.native_currency,
                    currencies,
                  );
                  return (
                    <tr key={order.id}>
                      <td>{row.asset.ticker}</td>
                      <td>
                        {simplifiedOptionSnapshotActionLabel(
                          order.transaction,
                          snapshot?.execution_effect || '',
                        )}
                      </td>
                      <td className="is-number">
                        {formatters.commasNoDigits(
                          simplifiedOptionSnapshotContracts(snapshot),
                        )}
                      </td>
                      <td className="is-number">
                        {money(snapshot?.signed_total_premium, symbol)}
                      </td>
                      <td>
                        {snapshot ? `${snapshot.days_to_expiry} days` : '—'}
                      </td>
                      <td className="is-number">
                        {money(snapshot?.display_payoff, symbol)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default OptionsMarket;
